import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CookieBanner from "@/components/site/CookieBanner";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import DemoDialog from "@/components/site/DemoDialog";
import { track, REGISTER_URL } from "@/lib/analytics";
import { useSeo, ORG_SCHEMA } from "@/lib/seo";

const Content = () => {
  const { t, lang } = useLang();
  useSeo(lang, {
    title: lang === "nl"
      ? "Over Tribeo | Marketplace voor makelaars en leveranciers"
      : "About Tribeo | Real Estate Marketplace Platform",
    description: lang === "nl"
      ? "Tribeo verbindt makelaars en leveranciers. Onafhankelijk Nederlands bedrijf in Den Haag, hosting in de EU. Eerlijk over wat we wel en niet doen."
      : "Tribeo connects real estate agents and service vendors. Independent Dutch company, EU hosting, no hidden costs. Find out who we are.",
    ogTitle: lang === "nl"
      ? "Tribeo — gebouwd voor makelaars en leveranciers."
      : "Tribeo — built for agents and vendors.",
    ogDescription: lang === "nl"
      ? "Een makelaarskantoor kan meer zijn dan de transactie alleen. Tribeo bouwt het platform dat dat mogelijk maakt."
      : "A real estate agent sells homes once. Everything after that passes them by. Tribeo fixes that.",
    keywords: lang === "nl" ? "over tribeo, tribeo Den Haag, TNA BV" : "about tribeo, tribeo company, TNA BV The Hague",
    nlPath: "/about",
    enPath: "/en/about",
    jsonLd: ORG_SCHEMA,
  });

  return (
    <>
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-cream">
        <div className="container max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.05]">{t.about.title}</h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground">{t.about.lead}</p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background">
        <div className="container max-w-3xl space-y-10">
          {t.about.sections.map((s) => (
            <div key={s.h} className="rounded-3xl bg-secondary p-8">
              <h2 className="text-2xl font-extrabold">{s.h}</h2>
              <p className="mt-3 text-base md:text-lg leading-relaxed text-foreground/80">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background">
        <div className="container max-w-3xl text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">{t.cta.title}</h2>
          <p className="mt-4 text-lg text-muted-foreground">{t.cta.desc}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("register_click", "about_bottom")}>
              <Button size="lg" className="rounded-full h-12 px-7 text-base font-bold w-full sm:w-auto">
                {t.cta.button} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </a>
            <DemoDialog
              trigger={
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base font-bold">
                  {t.cta.secondary}
                </Button>
              }
            />
          </div>
        </div>
      </section>
    </>
  );
};

const About = () => (
  <LanguageProvider>
    <div className="min-h-screen bg-background">
      <Header />
      <main><Content /></main>
      <Footer />
      <CookieBanner />
    </div>
  </LanguageProvider>
);

export default About;
